// components/OrderDetailsModal.jsx
import React, { useState, useEffect } from 'react';
import { apiCall } from '../utils/api';
import toast from 'react-hot-toast';
import { FaArrowUp, FaArrowDown, FaSearch, FaTimes, FaChevronDown } from "react-icons/fa";

const OrderDetailsModal = ({ isOpen, onClose, orderId }) => {
    const [loading, setLoading] = useState(false);
    const [orderData, setOrderData] = useState(null);
    const [logs, setLogs] = useState([]);
    const [error, setError] = useState(null);
    const [selectedStatus, setSelectedStatus] = useState('');
    const [txnInput, setTxnInput] = useState('');
    const [updating, setUpdating] = useState(false);
    const [activeTab, setActiveTab] = useState('basic');

    useEffect(() => {
        if (isOpen && orderId) {
            fetchOrderDetails();
        }
    }, [isOpen, orderId]);

    const fetchOrderDetails = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await apiCall(
                "order-details",
                {
                    order_id: orderId,
                },
                true
            );

            if (response.status === true) {
                setOrderData(response.data);
                setLogs(response.logs || []);
                setSelectedStatus(response.data.statusId || '');
                setTxnInput(response.data.txn || '');
            } else {
                setError(response.message || "Failed to fetch order details");
                toast.error(response.message || "Failed to fetch order details");
            }
        } catch (error) {
            setError("Network error. Please try again.");
            toast.error("Network error. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    const handleStatusUpdate = async () => {
        if (!selectedStatus) {
            toast.error("Please select a status");
            return;
        }

        if (selectedStatus === '1' && !txnInput.trim()) {
            toast.error("Please enter TXN ID for Success status");
            return;
        }

        setUpdating(true);
        try {
            const response = await apiCall(
                "order-update-status",
                {
                    order_id: orderData.orderId,
                    status: selectedStatus,
                    txn: txnInput,
                    table: 'order'
                },
                true
            );
            

            if (response.status === true) {
                toast.success("Status updated successfully!");
                fetchOrderDetails();
            } else {
                toast.error(response.message || "Failed to update status");
            }
        } catch (error) {
            toast.error("Network error. Please try again.");
        } finally {
            setUpdating(false);
        }
    };

    if (!isOpen) return null;

    const getStatusColor = (status) => {
        const colors = {
            'Success': 'bg-green-100 text-green-800',
            'Failed': 'bg-red-100 text-red-800',
            'Pending': 'bg-yellow-100 text-yellow-800',
            'Initiated': 'bg-blue-100 text-blue-800'
        };
        return colors[status] || 'bg-gray-100 text-gray-800';
    };

    const getStatusBadge = (status) => {
        const statusMap = {
            'Success': 'bg-green-100 text-green-800',
            'Failed': 'bg-red-100 text-red-800',
            'Pending': 'bg-yellow-100 text-yellow-800',
            'Initiated': 'bg-blue-100 text-blue-800',
            'Regular': 'bg-green-100 text-green-800'
        };
        return statusMap[status] || 'bg-gray-100 text-gray-800';
    };

    const formatLogResponse = (response) => {
        try {
            const parsed = JSON.parse(response);
            return JSON.stringify(parsed, null, 2);
        } catch {
            return response;
        }
    };

    const tabs = [
        { id: 'basic', label: 'Basic Transaction' },
        { id: 'user', label: 'User Data' },
        { id: 'payment', label: 'Payment Details' },
        { id: 'other', label: 'Other Details' },
        { id: 'logs', label: 'Transaction Logs' }
    ];

    const renderContent = () => {
        if (loading) {
            return (
                <div className="flex flex-col items-center justify-center py-20">
                    <div className="relative">
                        {/* Outer ring */}
                        <svg
                            className="h-12 w-12"
                            fill="none"
                            viewBox="0 0 24 24"
                        >
                            <circle
                                cx="12"
                                cy="12"
                                r="10"
                                stroke="#E5E7EB"
                                strokeWidth="1.5"
                            />
                        </svg>

                        {/* Spinning inner ring */}
                        <svg
                            className="absolute top-0 left-0 h-12 w-12 animate-spin"
                            fill="none"
                            viewBox="0 0 24 24"
                        >
                            <circle
                                cx="12"
                                cy="12"
                                r="10"
                                stroke="#2563EB"
                                strokeWidth="1.5"
                                strokeDasharray="30 100"
                                strokeLinecap="round"
                            />
                        </svg>
                    </div>
                    <span className="mt-4 text-sm font-medium text-gray-600 animate-pulse">
                        Loading...
                    </span>
                </div>
            );
        }

        if (error) {
            return (
                <div className="text-center py-20">
                    <svg className="w-16 h-16 text-red-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <p className="text-red-600">{error}</p>
                    <button
                        onClick={fetchOrderDetails}
                        className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                        Try Again
                    </button>
                </div>
            );
        }

        if (!orderData) {
            return (
                <div className="text-center py-20">
                    <p className="text-gray-500">No data available</p>
                </div>
            );
        }

        switch (activeTab) {
            case 'basic':
                return renderBasicTransaction();
            case 'user':
                return renderUserData();
            case 'payment':
                return renderPaymentDetails();
            case 'other':
                return renderOtherDetails();
            case 'logs':
                return renderLogs();
            default:
                return null;
        }
    };

    const renderTable = (data) => {
        if (!data || Object.keys(data).length === 0) {
            return (
                <div className="text-center py-8 text-gray-500">
                    No data available
                </div>
            );
        }

        return (
            <div className="overflow-x-auto border border-gray-200 rounded-lg">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                        <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Field</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Value</th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {Object.entries(data).map(([key, value]) => (
                            <tr key={key}>
                                <td className="px-4 py-3 text-sm font-medium text-gray-700">{key}</td>
                                <td className="px-4 py-3 text-sm text-gray-900 break-all">{value || 'N/A'}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
    };

    const renderBasicTransaction = () => {
        const data = {
            'Order ID': orderData.orderId,
            'Client Order ID': orderData.clientOrderId,
            'Order Type': orderData.orderType,
            'Order Date': orderData.orderDate,
            'Order Time': orderData.orderTime,
            'TXN': orderData.txn,
            'CN': orderData.cn,
            'Consumer Name': orderData.consumerName,
            'Amount': `₹${parseFloat(orderData.amount || 0).toFixed(2)}`,
            'Status': (
                <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(orderData.statusId)}`}>
                    {orderData.statusId || 'N/A'}
                </span>
            ),
            'Platform': orderData.platform,
            'Response Time': orderData.responseTime
        };

        return (
            <div className="space-y-4">
                <div className="overflow-x-auto border border-gray-200 rounded-lg">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Field</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Value</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {Object.entries(data).map(([key, value]) => (
                                <tr key={key}>
                                    <td className="px-4 py-3 text-sm font-medium text-gray-700">{key}</td>
                                    <td className="px-4 py-3 text-sm text-gray-900 break-all">{value}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        );
    };

    const renderUserData = () => {
        const data = {
            'User ID': orderData.userId,
            'Retailer Name': orderData.retailerName,
            'Retailer Mobile': orderData.retailerMobile,
            'Retailer Email': orderData.retailerEmail,
            'Retailer Role': orderData.retailerRole,
            'Retailer Status': (
                <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusBadge(orderData.retailerStatus)}`}>
                    {orderData.retailerStatus || 'N/A'}
                </span>
            ),
            'Retailer Balance': `₹${parseFloat(orderData.retailerBalance || 0).toFixed(2)}`,
            'Mobile Verification': orderData.retailerMobileVerification === '1' ? '✅ Verified' : '❌ Not Verified',
            'Email Verification': orderData.retailerEmailVerification === '1' ? '✅ Verified' : '❌ Not Verified',
            'KYC Verification': orderData.retailerKycVerification === '1' ? '✅ Verified' : '❌ Not Verified',
            'Registered Date': orderData.retailerRegisteredDate
        };

        return (
            <div className="space-y-4">
                <div className="overflow-x-auto border border-gray-200 rounded-lg">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Field</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Value</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {Object.entries(data).map(([key, value]) => (
                                <tr key={key}>
                                    <td className="px-4 py-3 text-sm font-medium text-gray-700">{key}</td>
                                    <td className="px-4 py-3 text-sm text-gray-900 break-all">{value}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        );
    };


    const renderPaymentDetails = () => {
        const renderProfitWithArrow = (value) => {
            const numValue = parseFloat(value || 0);
            const formattedValue = `₹${Math.abs(numValue).toFixed(2)}`;

            if (numValue > 0) {
                return (
                    <span className="flex items-center text-green-600 font-medium">
                        <FaArrowUp className="mr-1.5 text-xs" />
                        {formattedValue}
                    </span>
                );
            } else if (numValue < 0) {
                return (
                    <span className="flex items-center text-red-600 font-medium">
                        <FaArrowDown className="mr-1.5 text-xs" />
                        {formattedValue}
                    </span>
                );
            } else {
                return <span className="text-gray-500">₹0.00</span>;
            }
        };

        const renderValue = (key, value) => {
            if (key === 'orderType') {
                return value || 'N/A';
            }
            return `₹${parseFloat(value || 0).toFixed(2)}`;
        };

        const mainFields = [
            'orderType',
            'previousBalance',
            'debit',
            'credit',
            'balanceAfter'
        ];

        const profitFields = [
            'userProfit',
            'adminProfit',
            'agentProfit'
        ];

        const fieldLabels = {
            orderType: 'Order Type',
            previousBalance: 'Previous Balance',
            debit: 'Debit',
            credit: 'Credit',
            balanceAfter: 'Balance After',
            userProfit: 'User Profit',
            adminProfit: 'Admin Profit',
            agentProfit: 'Agent Profit'
        };

        return (
            <div className="space-y-4">
                {/* Main Details Table */}
                {/* Main Details Table */}
                <div className="overflow-x-auto border border-gray-200 rounded-lg shadow-sm">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                {mainFields.map((key) => (
                                    <th key={key} className="px-4 py-3 text-left text-sm font-medium text-gray-700">
                                        {fieldLabels[key]}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            <tr>
                                {mainFields.map((key) => (
                                    <td key={key} className="px-4 py-3 text-sm text-gray-900">
                                        {renderValue(key, orderData[key])}
                                    </td>
                                ))}
                            </tr>
                        </tbody>
                    </table>
                </div>

                {/* Profit Details Table with Arrows */}
                <div className="overflow-x-auto border border-gray-200 rounded-lg shadow-sm">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Field
                                </th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Value
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {profitFields.map((key) => (
                                <tr key={key}>
                                    <td className="px-4 py-3 text-sm font-medium text-gray-700">
                                        {fieldLabels[key]}
                                    </td>
                                    <td className="px-4 py-3 text-sm">
                                        {renderProfitWithArrow(orderData[key])}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        );
    };

    const renderOtherDetails = () => {
        const data = {
            'Operator': (
                <div className="flex items-center gap-2">
                    {orderData.operatorLogo && (
                        <img
                            src={orderData.operatorLogo}
                            alt={orderData.operatorName}
                            className="w-6 h-6 rounded-full object-contain"
                            onError={(e) => { e.target.style.display = 'none'; }}
                        />
                    )}
                    <span>{orderData.operatorName || 'N/A'}</span>
                </div>
            ),
            'Operator ID': orderData.operatorId,
            'Provider': orderData.providerName,
            'Provider ID': orderData.providerId,
            'Provider Unique ID': orderData.providerUniqueId,
            'Reference Order ID': orderData.referenceOrderId,
            'Refund Date': orderData.refundDate,
            'Refund Time': orderData.refundTime,
            'Remark Code': orderData.remarkCode,
            'Update By': orderData.updateBy,
            'Update By ID': orderData.updateById
        };

        return (
            <div className="space-y-4">
                <div className="overflow-x-auto border border-gray-200 rounded-lg">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Field</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Value</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {Object.entries(data).map(([key, value]) => (
                                <tr key={key}>
                                    <td className="px-4 py-3 text-sm font-medium text-gray-700">{key}</td>
                                    <td className="px-4 py-3 text-sm text-gray-900 break-all">{value}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        );
    };

    const renderLogs = () => {
        if (logs.length === 0) {
            return (
                <div className="text-center py-12 text-gray-500">
                    No transaction logs available
                </div>
            );
        }

        return (
            <div className="space-y-4 max-h-[500px] overflow-y-auto pr-2">
                {logs.map((log, index) => (
                    <div key={index} className="border border-gray-200 rounded-lg overflow-hidden flex-shrink-0">
                        <div className="bg-gray-50 px-4 py-2 flex flex-wrap items-center justify-between border-b border-gray-200 gap-2">
                            <span className="text-xs font-medium text-gray-700">Provider: {log.name || 'N/A'} ({log.provider_id || 'N/A'})</span>
                        </div>
                        <div className="p-4 space-y-3">
                            {log.request && (
                                <div>
                                    <label className="text-xs font-medium text-gray-600">Request</label>
                                    <pre className="mt-1 p-3 bg-gray-50 rounded-lg text-xs overflow-x-auto max-h-32 whitespace-pre-wrap break-all">
                                        {formatLogResponse(log.request)}
                                    </pre>
                                </div>
                            )}
                            {log.response && (
                                <div>
                                    <label className="text-xs font-medium text-gray-600">Response</label>
                                    <pre className="mt-1 p-3 bg-green-50 rounded-lg text-xs overflow-x-auto max-h-32 whitespace-pre-wrap break-all">
                                        {formatLogResponse(log.response)}
                                    </pre>
                                </div>
                            )}
                            {log.client_payload && (
                                <div>
                                    <label className="text-xs font-medium text-gray-600">Client Payload</label>
                                    <pre className="mt-1 p-3 bg-yellow-50 rounded-lg text-xs overflow-x-auto max-h-32 whitespace-pre-wrap break-all">
                                        {formatLogResponse(log.client_payload)}
                                    </pre>
                                </div>
                            )}
                        </div>
                    </div>
                ))}
            </div>
        );
    };

    return (
        <div className="fixed inset-0 z-50 overflow-y-auto">
            {/* Backdrop with fade */}
            <div
                className="fixed inset-0 bg-black/50 transition-opacity duration-300 ease-in-out"
                onClick={onClose}
                style={{
                    opacity: isOpen ? 1 : 0,
                }}
            ></div>

            {/* Modal with fade and scale */}
            <div className="flex min-h-full items-center justify-center p-4">
                <div
                    className="relative bg-white rounded-2xl shadow-2xl max-w-7xl w-full max-h-[90vh] overflow-hidden flex flex-col transition-all duration-300 ease-in-out"
                    style={{
                        opacity: isOpen ? 1 : 0,
                        transform: isOpen ? 'scale(1)' : 'scale(0.95)',
                    }}
                >
                    {/* Header - Sticky */}
                    <div className="flex-shrink-0 sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center z-10">
                        <div>
                            <h3 className="text-xl font-semibold text-gray-900">
                                Order Details
                            </h3>
                            <p className="text-sm text-gray-500">Order ID: #{orderData?.orderId || orderId}</p>
                        </div>
                        <button
                            onClick={onClose}
                            className="text-gray-400 hover:text-gray-600 transition-colors p-2 hover:bg-gray-100 rounded-lg"
                        >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>

                    {/* Status Update Section - Sticky */}
                    <div className="flex-shrink-0 bg-gradient-to-r from-blue-50 to-indigo-50 px-6 py-4 border-b border-blue-200 sticky top-[73px] z-5">
                        <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center">
                            Update Status
                        </h4>
                        <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-end">
                            <div className="flex-1 w-full sm:w-auto">
                                <label className="block text-xs font-medium text-gray-600 mb-1">Status</label>
                                <select
                                    value={selectedStatus}
                                    onChange={(e) => setSelectedStatus(e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm bg-white"
                                    disabled={updating || loading}
                                >
                                    <option disabled value="Initiated">Initiated</option>
                                    <option disabled value="Pending">Pending</option>
                                    <option value="1">Success</option>
                                    <option value="2">Failed</option>
                                </select>
                            </div>
                            {selectedStatus === '1' && (
                                <div className="flex-1 w-full sm:w-auto">
                                    <label className="block text-xs font-medium text-gray-600 mb-1">TXN ID</label>
                                    <input
                                        type="text"
                                        value={txnInput}
                                        onChange={(e) => setTxnInput(e.target.value)}
                                        placeholder="Enter TXN ID"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm bg-white"
                                        disabled={updating || loading}
                                    />
                                </div>
                            )}
                            <button
                                onClick={handleStatusUpdate}
                                disabled={updating || loading}
                                className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-all duration-200 text-sm disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap shadow-sm hover:shadow"
                            >
                                {updating ? (
                                    <span className="flex items-center">
                                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                        Updating...
                                    </span>
                                ) : 'Update Status'}
                            </button>
                        </div>
                    </div>

                    {/* Tabs - Sticky */}
                    <div className="flex-shrink-0 bg-white border-b border-gray-200 px-6 sticky top-[141px] z-5">
                        <div className="flex space-x-1 overflow-x-auto scrollbar-hide">
                            {tabs.map((tab) => (
                                <button
                                    key={tab.id}
                                    onClick={() => setActiveTab(tab.id)}
                                    className={`
                                        px-4 py-3 text-sm font-medium transition-all duration-200 whitespace-nowrap
                                        ${activeTab === tab.id
                                            ? 'text-blue-600 border-b-2 border-blue-600'
                                            : 'text-gray-500 hover:text-gray-700 hover:border-b-2 hover:border-gray-300'
                                        }
                                    `}
                                >
                                    {tab.label}
                                    {tab.id === 'logs' && logs.length > 0 && (
                                        <span className="ml-1.5 px-1.5 py-0.5 text-xs bg-gray-100 text-gray-600 rounded-full">
                                            {logs.length}
                                        </span>
                                    )}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Content - Scrollable */}
                    <div className="flex-1 overflow-y-auto p-6">
                        {renderContent()}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default OrderDetailsModal;